import { TicketItem } from "@features/cartDetails/types";
import { SparklesIcon } from "@heroicons/react/20/solid";
import { useNotificationStore } from "@store/notificationStore";
import { PriceStatus } from "@utils/enums";
import { formatPrice, joinClassNames } from "@utils/helpers";
import { motion } from "framer-motion";
import { useState } from "react";
import AddTicketDropdown from "./addTicketDropdown";
import FlexiPricePopover from "./flexiPricePopover";
import MobileTicketDisplay from "./mobileTicketDisplay";
import { FormattedPrice } from "@features/eventDisplayGA/types";
import { handleAddCart } from "@features/eventDisplayGA/utils/cartUtils";
import { Badge, Card, Text } from "@components/theming";
import { useLocale, useTranslations } from "next-intl";
import { useSWRConfig } from "swr";
import { useIsShopsite } from "@context/DomainContext";
import { Analytics, EventLandingPage } from "@types";
import { format, isFuture, parseISO } from "date-fns";

interface Props {
  prices: FormattedPrice[];
  eventId: number;
  eventName: string;
  eventVenueId: number;
  orgUid: string;
  showingId: number;
  basketTickets: TicketItem[];
  selectedConnectedShowings: number[] | null;
  analyticsConfigs?: Analytics[];
  landingPages?: EventLandingPage[];
  onAddToCart?: () => void;
}

const EventGASelection = ({
  prices,
  eventId,
  eventName,
  eventVenueId,
  orgUid,
  showingId,
  basketTickets,
  selectedConnectedShowings,
  analyticsConfigs,
  landingPages,
  onAddToCart,
}: Props) => {
  const { cache } = useSWRConfig();
  const t = useTranslations("EventPage.gaselection");
  const notification_translations = useTranslations("EventPage.notifications");
  const [hoveredBadge, setHoveredBadge] = useState<string | null>(null);
  const { setFailure, setSuccess, setActionSuccess } = useNotificationStore(
    (state) => state
  );
  const locale = useLocale();
  const isShopsite = useIsShopsite();

  const isComboShowing =
    !!selectedConnectedShowings && selectedConnectedShowings.length > 0;

  const cache_key = Array.from(cache.keys()).filter((key) =>
    key.includes(
      `events/${eventId}/${eventVenueId}/showings/${showingId}/tickets/ga`
    )
  );

  const selected_quantities = prices.map((p) => ({
    id: p.id,
    qty: isComboShowing ? 0 : p.bookedTickets || 0,
    qty_from_combo: p.bookedFromCombo,
    actual_booked_qty: p.bookedTickets || 0,
  }));

  const putQtyInCart = async (
    price: FormattedPrice,
    qty: string,
    isDynamic?: boolean
  ) => {
    let ticketCountForTranslation = parseInt(qty) * 1;
    if (!isDynamic) {
      ticketCountForTranslation =
        ticketCountForTranslation -
        (selected_quantities.find((sq) => sq.id === price.id)?.qty || 0);
    }
    if (ticketCountForTranslation < 0) {
      ticketCountForTranslation = Math.abs(ticketCountForTranslation);
    }

    return handleAddCart(
      eventId,
      eventName,
      eventVenueId,
      orgUid,
      showingId,
      selected_quantities.find((sq) => sq.id === price.id)?.qty,
      setSuccess,
      setFailure,
      setActionSuccess,
      basketTickets,
      price,
      qty,
      selectedConnectedShowings,
      isDynamic,
      {
        removedcombo: {
          success: notification_translations("removedcombo.success", {
            count: ticketCountForTranslation,
          }),
          failure: notification_translations("removedcombo.failure", {
            count: ticketCountForTranslation,
          }),
        },
        removedtickets: {
          success: notification_translations("removedtickets.success", {
            count: ticketCountForTranslation,
          }),
          failure: notification_translations("removedtickets.failure", {
            count: ticketCountForTranslation,
          }),
        },
        addedtickets: {
          success: notification_translations("addedtickets.success", {
            count: ticketCountForTranslation,
          }),
          failure: notification_translations("addedtickets.failure", {
            count: ticketCountForTranslation,
          }),
        },
      },
      cache.get(cache_key[0])?.data,
      analyticsConfigs,
      landingPages,
      onAddToCart
    );
  };

  return (
    <div className="relative">
      <Card
        size="2"
        sharedClasses="max-sm:hidden"
        shopsiteClasses="border bg-white rounded-lg"
        whitelabelClasses="!p-0"
      >
        <div
          className={joinClassNames(
            "flex p-4 w-full border-b",
            isShopsite ? "" : "border-[color:var(--gray-5)]"
          )}
        >
          <Text
            as="div"
            size="2"
            weight="medium"
            shopsiteClasses="text-sm font-medium"
            sharedClasses="w-36 mr-4"
          >
            {t("type")}
          </Text>
          <Text
            as="div"
            size="2"
            weight="medium"
            shopsiteClasses="text-sm font-medium"
            sharedClasses="block flex-1"
          >
            {t("info")}
          </Text>
          <Text
            as="div"
            size="2"
            weight="medium"
            shopsiteClasses="text-sm font-medium"
            sharedClasses="text-sm w-32 mr-4 text-right"
          >
            {t("price")}
          </Text>
          <div className="w-16" />
        </div>
        <div
          className={joinClassNames(
            "block divide-y",
            isShopsite ? "" : "divide-[color:var(--gray-5)]"
          )}
        >
          {prices
            ?.sort((a, b) => a.order - b.order)
            .map((price, index) => (
              <div
                key={price.id}
                className={joinClassNames(
                  "flex items-center justify-between p-4 w-full",
                  (
                    price.isCombo
                      ? price.max === 0 || price.max === price.bookedTickets
                      : (price.status !== PriceStatus.AVAILABLE &&
                          selected_quantities.find((sq) => sq.id === price.id)
                            ?.qty === 0) ||
                        (price.bookingLimit ?? price.max) < price.min
                  )
                    ? isShopsite
                      ? "bg-gray-100"
                      : "bg-[color:var(--gray-a3)]"
                    : "",
                  index === prices.length - 1 && isShopsite
                    ? "rounded-b-[7px]"
                    : ""
                )}
              >
                {/* ── Ticket type name + status badge ───────────────────────── */}
                <Text
                  as="div"
                  size="2"
                  weight="bold"
                  sharedClasses="text-left w-36 mr-4"
                  shopsiteClasses="text-sm font-semibold"
                >
                  <span className="flex flex-col gap-0.5 w-fit">
                    <Text
                      as="span"
                      whitelabelClasses={
                        (
                          price.isCombo
                            ? price.max === 0 ||
                              price.max === price.bookedTickets
                            : (price.status !== PriceStatus.AVAILABLE &&
                                selected_quantities.find(
                                  (sq) => sq.id === price.id
                                )?.qty === 0) ||
                              (price.bookingLimit ?? price.max) < price.min
                        )
                          ? "text-[color:var(--gray-11)]"
                          : ""
                      }
                    >
                      {price.type}
                    </Text>
                    {price.isCombo ? (
                      // Combo sold-out / max-bought badges
                      price.max === 0 ? (
                        <Badge
                          color="red"
                          shopsiteClasses="!py-1 !rounded-lg w-fit"
                        >
                          <Text
                            size="2"
                            weight="bold"
                            shopsiteClasses="text-sm font-semibold"
                          >
                            {t("soldout")}
                          </Text>
                        </Badge>
                      ) : price.max === price.bookedTickets ? (
                        <Badge
                          color="red"
                          shopsiteClasses="!py-1 !rounded-lg w-fit"
                        >
                          <Text
                            size="2"
                            weight="bold"
                            shopsiteClasses="text-sm font-semibold"
                          >
                            {t("maxbought")}
                          </Text>
                        </Badge>
                      ) : null
                    ) : (
                      // Non-combo status badge
                      ((price.status !== PriceStatus.AVAILABLE &&
                        selected_quantities.find((sq) => sq.id === price.id)
                          ?.qty === 0) ||
                        (price.bookingLimit ?? price.max) < price.min) && (
                        <>
                          {/* Upcoming: amber badge*/}
                          {price.status === PriceStatus.UPCOMING ? (
                            <Badge
                              color="amber"
                              sharedClasses="w-fit"
                              shopsiteClasses="!py-1 !rounded-lg"
                            >
                              <Text
                                size="2"
                                weight="bold"
                                shopsiteClasses="text-sm font-semibold"
                              >
                                {t("notavailable")}
                              </Text>
                            </Badge>
                          ) : price.status === PriceStatus.AWAITINGTRIGGER ? (
                            // AwaitingTrigger: purple badge
                            <Badge
                              color="purple"
                              sharedClasses="w-fit"
                              shopsiteClasses="!py-1 !rounded-lg"
                            >
                              <Text
                                size="2"
                                weight="bold"
                                shopsiteClasses="text-sm font-semibold"
                              >
                                {t("awaitingtrigger")}
                              </Text>
                            </Badge>
                          ) : (
                            // Legacy statuses — red badge
                            <Badge
                              color="red"
                              sharedClasses="w-fit"
                              shopsiteClasses="!py-1 !rounded-lg"
                            >
                              <Text
                                size="2"
                                weight="bold"
                                shopsiteClasses="text-sm font-semibold"
                              >
                                {price.status === PriceStatus.NOTAVAILABLE
                                  ? t("notavailable")
                                  : price.status === PriceStatus.SOLDOUT
                                  ? t("soldout")
                                  : price.status === PriceStatus.BOOKEDOUT
                                  ? t("bookedout")
                                  : t("limitreached")}
                              </Text>
                            </Badge>
                          )}
                        </>
                      )
                    )}
                  </span>
                </Text>
                {/* ── Info column: description + all info badges ─────────────── */}
                <div
                  className={joinClassNames(
                    "flex flex-col gap-2 line-clamp-2 flex-1 text-left mr-4 text-sm",
                    isShopsite ? "text-gray-500" : "text-[color:var(--gray-11)]"
                  )}
                >
                  {price.info ? <span>{price.info}</span> : <></>}
                  <div
                    className={joinClassNames(
                      "flex gap-2",
                      !isShopsite ? "flex-wrap" : ""
                    )}
                  >
                    {/* Promo badge */}
                    {price.isPromo ? (
                      <Badge
                        asChild
                        color="yellow"
                        size="2"
                        sharedClasses="cursor-default"
                        shopsiteClasses={joinClassNames(
                          "!rounded-lg truncate font-semibold w-fit transition-opacity ease-in-out duration-200",
                          hoveredBadge === `${price.id}-promo` ||
                            hoveredBadge === null ||
                            !hoveredBadge.includes(price.id)
                            ? "opacity-100"
                            : "opacity-70"
                        )}
                      >
                        <motion.span
                          onHoverStart={() =>
                            setHoveredBadge(`${price.id}-promo`)
                          }
                          onHoverEnd={() => setHoveredBadge(null)}
                          animate={
                            hoveredBadge === `${price.id}-promo`
                              ? { flexShrink: 0 }
                              : { flexShrink: 1 }
                          }
                        >
                          <Text
                            as="span"
                            size="2"
                            weight="bold"
                            sharedClasses="flex gap-1 items-center w-fit truncate"
                          >
                            <SparklesIcon className="h-4 w-4" />
                            {t("promo")}
                            <SparklesIcon className="h-4 w-4" />
                          </Text>
                        </motion.span>
                      </Badge>
                    ) : (
                      <></>
                    )}
                    {/* Combo badge */}
                    {price.isCombo ? (
                      <Badge
                        asChild
                        color="green"
                        size="2"
                        sharedClasses="cursor-default"
                        shopsiteClasses={joinClassNames(
                          "!rounded-lg truncate font-semibold w-fit transition-opacity ease-in-out duration-200",
                          hoveredBadge === `${price.id}-combo` ||
                            hoveredBadge === null ||
                            !hoveredBadge.includes(price.id)
                            ? "opacity-100"
                            : "opacity-70"
                        )}
                      >
                        <motion.span
                          onHoverStart={() =>
                            setHoveredBadge(`${price.id}-combo`)
                          }
                          onHoverEnd={() => setHoveredBadge(null)}
                          animate={
                            hoveredBadge === `${price.id}-combo`
                              ? { flexShrink: 0 }
                              : { flexShrink: 1 }
                          }
                        >
                          <Text
                            as="span"
                            size="2"
                            weight="bold"
                            sharedClasses="w-fit truncate"
                          >
                            {t("combo")}
                          </Text>
                        </motion.span>
                      </Badge>
                    ) : (
                      <></>
                    )}
                    {/* Area badge */}
                    {price.areaName ? (
                      <Badge
                        asChild
                        color="sky"
                        size="2"
                        sharedClasses="cursor-default"
                        shopsiteClasses={joinClassNames(
                          "!rounded-lg truncate font-semibold w-fit transition-opacity ease-in-out duration-200",
                          hoveredBadge === `${price.id}-area` ||
                            hoveredBadge === null ||
                            !hoveredBadge.includes(price.id)
                            ? "opacity-100"
                            : "opacity-70"
                        )}
                      >
                        <motion.span
                          onHoverStart={() =>
                            setHoveredBadge(`${price.id}-area`)
                          }
                          onHoverEnd={() => setHoveredBadge(null)}
                          animate={
                            hoveredBadge === `${price.id}-area`
                              ? { flexShrink: 0 }
                              : { flexShrink: 1 }
                          }
                        >
                          <Text
                            as="span"
                            size="2"
                            weight="bold"
                            sharedClasses="w-fit truncate"
                          >
                            {price.areaName}
                          </Text>
                        </motion.span>
                      </Badge>
                    ) : (
                      <></>
                    )}
                    {/* Price scheme badge */}
                    {price.priceSchemeName ? (
                      <Badge
                        asChild
                        color="cyan"
                        size="2"
                        sharedClasses="cursor-default"
                        shopsiteClasses={joinClassNames(
                          "!rounded-lg truncate font-semibold w-fit transition-opacity ease-in-out duration-200",
                          hoveredBadge === `${price.id}-price_scheme` ||
                            hoveredBadge === null ||
                            !hoveredBadge.includes(price.id)
                            ? "opacity-100"
                            : "opacity-70"
                        )}
                      >
                        <motion.span
                          onHoverStart={() =>
                            setHoveredBadge(`${price.id}-price_scheme`)
                          }
                          onHoverEnd={() => setHoveredBadge(null)}
                          animate={
                            hoveredBadge === `${price.id}-price_scheme`
                              ? { flexShrink: 0 }
                              : { flexShrink: 1 }
                          }
                        >
                          <Text
                            as="span"
                            size="2"
                            weight="bold"
                            sharedClasses="w-fit truncate"
                          >
                            {price.priceSchemeName}
                          </Text>
                        </motion.span>
                      </Badge>
                    ) : (
                      <></>
                    )}
                    {/* Price section badge */}
                    {price.priceSectionName ? (
                      <Badge
                        asChild
                        color="teal"
                        size="2"
                        sharedClasses="cursor-default"
                        shopsiteClasses={joinClassNames(
                          "!rounded-lg truncate font-semibold w-fit transition-opacity ease-in-out duration-200",
                          hoveredBadge === `${price.id}-price_section` ||
                            hoveredBadge === null ||
                            !hoveredBadge.includes(price.id)
                            ? "opacity-100"
                            : "opacity-70"
                        )}
                      >
                        <motion.span
                          onHoverStart={() =>
                            setHoveredBadge(`${price.id}-price_section`)
                          }
                          onHoverEnd={() => setHoveredBadge(null)}
                          animate={
                            hoveredBadge === `${price.id}-price_section`
                              ? { flexShrink: 0 }
                              : { flexShrink: 1 }
                          }
                        >
                          <Text
                            as="span"
                            size="2"
                            weight="bold"
                            sharedClasses="w-fit truncate"
                          >
                            {price.priceSectionName}
                          </Text>
                        </motion.span>
                      </Badge>
                    ) : (
                      <></>
                    )}
                    {/*
                     * Upcoming: amber info badge showing when the area opens.
                     * Uses availableFrom (area-level SP date) in preference to
                     * priceAgeStartDate (price-level date) so the date is always correct.
                     * Only shown when status is UPCOMING — the type badge above already
                     * covers the "Upcoming" label, so this badge carries the date.
                     */}
                    {price.status === PriceStatus.UPCOMING &&
                    (price.availableFrom || price.priceAgeStartDate) ? (
                      <Badge
                        asChild
                        color="amber"
                        size="2"
                        sharedClasses="cursor-default"
                        shopsiteClasses={joinClassNames(
                          "!rounded-lg truncate font-semibold w-fit transition-opacity ease-in-out duration-200",
                          hoveredBadge === `${price.id}-upcoming` ||
                            hoveredBadge === null ||
                            !hoveredBadge.includes(price.id)
                            ? "opacity-100"
                            : "opacity-70"
                        )}
                      >
                        <motion.span
                          onHoverStart={() =>
                            setHoveredBadge(`${price.id}-upcoming`)
                          }
                          onHoverEnd={() => setHoveredBadge(null)}
                          animate={
                            hoveredBadge === `${price.id}-upcoming`
                              ? { flexShrink: 0 }
                              : { flexShrink: 1 }
                          }
                        >
                          <Text
                            as="span"
                            size="2"
                            weight="bold"
                            sharedClasses="w-fit truncate"
                          >
                            {t("availability", {
                              preposition: "from",
                              date: format(
                                parseISO(
                                  price.availableFrom ??
                                    price.priceAgeStartDate ??
                                    ""
                                ),
                                "EEE dd MMM yyy"
                              ),
                            })}
                          </Text>
                        </motion.span>
                      </Badge>
                    ) : (
                      <></>
                    )}
                    {/*
                     * AwaitingTrigger: purple info badge.
                     */}
                    {/*
                     * Existing amber "from" date badge excludes
                     * UPCOMING rows (which have their own blue date badge above) to
                     * avoid showing two date badges on the same row. Only shown when
                     * the start date is still in the future — a past start date just
                     * means the price already went on sale, not that it's reopening.
                     */}
                    {price.priceAgeStartDate &&
                    price.status !== PriceStatus.AVAILABLE &&
                    price.status !== PriceStatus.UPCOMING &&
                    isFuture(parseISO(price.priceAgeStartDate)) ? (
                      <Badge
                        asChild
                        color="amber"
                        size="2"
                        sharedClasses="cursor-default"
                        shopsiteClasses={joinClassNames(
                          "!rounded-lg truncate font-semibold w-fit transition-opacity ease-in-out duration-200",
                          hoveredBadge === `${price.id}-start-date` ||
                            hoveredBadge === null ||
                            !hoveredBadge.includes(price.id)
                            ? "opacity-100"
                            : "opacity-70"
                        )}
                      >
                        <motion.span
                          onHoverStart={() =>
                            setHoveredBadge(`${price.id}-start-date`)
                          }
                          onHoverEnd={() => setHoveredBadge(null)}
                          animate={
                            hoveredBadge === `${price.id}-start-date`
                              ? { flexShrink: 0 }
                              : { flexShrink: 1 }
                          }
                        >
                          <Text
                            as="span"
                            size="2"
                            weight="bold"
                            sharedClasses="w-fit truncate"
                          >
                            {t("availability", {
                              preposition: "from",
                              date: price.priceAgeStartDate,
                            })}
                          </Text>
                        </motion.span>
                      </Badge>
                    ) : (
                      <></>
                    )}
                    {/*
                     * Amber "until" date badge. Only shown when the end date is
                     * still in the future — a past end date means the sale window
                     * already closed, not that it's reopening.
                     */}
                    {price.priceAgeEndDate &&
                    price.status !== PriceStatus.AVAILABLE &&
                    isFuture(parseISO(price.priceAgeEndDate)) ? (
                      <Badge
                        asChild
                        color="amber"
                        size="2"
                        sharedClasses="cursor-default"
                        shopsiteClasses={joinClassNames(
                          "!rounded-lg truncate font-semibold w-fit transition-opacity ease-in-out duration-200",
                          hoveredBadge === `${price.id}-end-date` ||
                            hoveredBadge === null ||
                            !hoveredBadge.includes(price.id)
                            ? "opacity-100"
                            : "opacity-70"
                        )}
                      >
                        <motion.span
                          onHoverStart={() =>
                            setHoveredBadge(`${price.id}-end-date`)
                          }
                          onHoverEnd={() => setHoveredBadge(null)}
                          animate={
                            hoveredBadge === `${price.id}-end-date`
                              ? { flexShrink: 0 }
                              : { flexShrink: 1 }
                          }
                        >
                          <Text
                            as="span"
                            size="2"
                            weight="bold"
                            sharedClasses="w-fit truncate"
                          >
                            {t("availability", {
                              preposition: "until",
                              date: price.priceAgeEndDate,
                            })}
                          </Text>
                        </motion.span>
                      </Badge>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
                {/* ── Price + quantity control ───────────────────────────────── */}
                {price.priceMaximum !== null &&
                price.priceMaximum > 0 &&
                price.priceMinimum !== null &&
                price.priceMinimum > 0 ? (
                  <span className="relative w-32 text-right text-sm tabular-nums">
                    <FlexiPricePopover
                      price={price}
                      selectedQuantities={selected_quantities}
                      putQtyInCart={putQtyInCart}
                    />
                  </span>
                ) : (
                  <>
                    <span className="relative w-32 pr-4 text-right text-sm tabular-nums">
                      {price.isCombo ? (
                        <div className="flex flex-col items-end">
                          <Text as="span">
                            {formatPrice(
                              locale,
                              price.currency.CurrencyCode,
                              price.discountPrice
                            )}
                          </Text>
                          {price.price > price.discountPrice ? (
                            <Text
                              as="span"
                              sharedClasses="text-xs font-semibold line-through"
                              whitelabelClasses="text-[color:var(--gray-9)]"
                              shopsiteClasses="text-gray-400"
                            >
                              {formatPrice(
                                locale,
                                price.currency.CurrencyCode,
                                price.price
                              )}
                            </Text>
                          ) : (
                            <></>
                          )}
                        </div>
                      ) : (
                        <Text as="span">
                          {formatPrice(
                            locale,
                            price.currency.CurrencyCode,
                            price.price
                          )}
                        </Text>
                      )}
                    </span>
                    <AddTicketDropdown
                      price={price}
                      putQtyInCart={putQtyInCart}
                      selectedQuantities={selected_quantities}
                      isComboShowing={isComboShowing}
                    />
                  </>
                )}
              </div>
            ))}
        </div>
      </Card>
      {/* Mobile layout */}
      <div className="flex flex-col gap-4 sm:hidden">
        {prices.map((price) => (
          <MobileTicketDisplay
            key={price.id}
            price={price}
            putQtyInCart={putQtyInCart}
            selectedQuantities={selected_quantities}
            isComboShowing={isComboShowing}
          />
        ))}
        <Text
          as="p"
          size="2"
          color="gray"
          sharedClasses="text-center"
          shopsiteClasses="text-sm text-gray-500"
        >
          {t("taptoselect")}
        </Text>
      </div>
    </div>
  );
};

export default EventGASelection;
